import Game from '../../Game';
import PIXIRenderer from '../Render/PIXIRenderer';
import Element from '../../Prototype/UI/Element';
import GameObject from '../../GameObject/GameObject';
import Renderable from '../../Component/Renderable';
import BaseUISystem from './BaseUISystem';
import Interactable from '../../Component/Interactable';
import StateComponent from '../../Component/State';
import { IGameState } from '../../State/Game/IGameState';
import { IUIState } from '../../State/UI/IUIState';
import CleanupManager from '../../Utility/CleanupManager';
import { ResourceDriver } from '../../Utility/Resource/ResourceManager';
import { PopupID } from '../../State/UI/PopupID';

export default class BaseUIHandler {
    public name: PopupID;

    public element: Element;

    public system: BaseUISystem;

    /**
     * True after the `create()`. False after `destroy()`. Elements only handle state changes while active.
     */
    public active: boolean = false;

    /**
     * True if handler should trigger `create()` method as soon as preload is complete.
     */
    public alwaysVisible: boolean = false;

    /**
     * Additional resources to be preloaded for this UI element.
     */
    protected resources: Map<string, string>;
    /**
     * Secondary resources to be preloaded for this UI element.
     */
    protected secondaryResources: Map<string, string>;

    private hasDataFile: boolean = false;

    protected UIData: any;
    protected readonly cleanupManager: CleanupManager = new CleanupManager();
    private interactive: boolean | null = null;

    constructor(
        name: PopupID,
        system: BaseUISystem,
        hasDataFile: boolean = true
    ) {
        this.name = name;
        this.system = system;
        this.resources = new Map<string, string>();
        this.secondaryResources = new Map<string, string>();
        this.hasDataFile = hasDataFile;
    }

    public async preload() {
        for (const [key, value] of this.resources.entries()) {
            Game.resource.add(key, value);
        }
        for (const [key, value] of this.secondaryResources.entries()) {
            Game.resource.add(key, value, ResourceDriver.SECONDARY);
        }

        if (this.hasDataFile) {
            const key = `resource/data/ui/${this.name}.json`;
            Game.resource.add(this.name, key);
        }

        await Game.resource.load();

        if (this.hasDataFile) {
            this.UIData = Game.resource.get<any>(this.name).rootElement;
        }
    }

    public create() {
        if (this.active) return;
        this.createElement();
        this.onCreate();

        if (this.element) {
            this.element.container.resize(
                PIXIRenderer.renderer.width,
                PIXIRenderer.renderer.height
            );
        }

        this.active = true;

        Game.resource.startOtherDriversLoad();
    }

    public destroy(): void {
        this.cleanupManager.cleanUpAll();
        this.onDestroy();
        this.active = false;
    }

    // tslint:disable-next-line: no-empty
    public handleStateChanged(state: IGameState): void {
        this.element
            .getComponentsInChildren(StateComponent)
            .forEach((component: StateComponent) =>
                component.handleStateChanged(state)
            );
    }

    // tslint:disable-next-line: no-empty
    public *show(state: IUIState): IterableIterator<number> {}
    // tslint:disable-next-line: no-empty
    public *hide(state: IUIState): IterableIterator<number> {}

    // tslint:disable-next-line: no-empty
    protected onCreate(): void {}

    // tslint:disable-next-line: no-empty
    protected onDestroy(): void {}

    // tslint:disable-next-line: no-empty
    public onShow(params?: any): void {}

    // tslint:disable-next-line: no-empty
    protected onShown(params?: any): void {}

    // tslint:disable-next-line: no-empty
    protected onHide(params?: any): void {}

    // tslint:disable-next-line: no-empty
    protected onHidden(params?: any): void {}

    protected createElement() {
        if (!this.UIData) {
            this.UIData = {
                name: `${this.name}_main_element`,
                layout: {
                    anchors: [0, 0, 0, 0],
                },
            };
        }

        this.element = GameObject.Instantiate(Element, this.UIData);

        this.element.getComponent(Renderable).visible = this.alwaysVisible;

        this.system.scene.addGameObject(this.element);
    }

    protected toggleInteractables(value: boolean, lock?: boolean): void {
        if (this.interactive === value) return;
        this.interactive = value;

        const interactables: Interactable[] = this.element.getComponentsInChildren(
            Interactable
        );
        interactables.forEach((interactable: Interactable) => {
            if (!interactable.toggleable) return;
            interactable.toggle(lock ? value : undefined);
        });
    }

    protected restoreInteractivity(): void {
        this.toggleInteractables(true, false);
    }
    protected disableInteractivity(): void {
        this.toggleInteractables(false, true);
    }
}
